// ─────────────────────────────────────────────────────────────
// KeywordGod — Shared Utils
// 등급 산출, 숫자 포맷, 정규화 헬퍼 (브라우저/워커 양쪽 OK)
// ─────────────────────────────────────────────────────────────

import {
  GRADE_THRESHOLDS,
  SATURATION_THRESHOLDS,
  COMP_IDX_NUMERIC,
} from './constants';
import type { CompetitionLevel, KeywordGrade, SaturationGrade } from './types';

/** [lo, hi] 구간으로 클램프 */
export function clamp(n: number, lo = 0, hi = 1): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

/** value / max 를 0~1 로 정규화. value가 max 이상이면 1. */
export function normalize(value: number, max = 100): number {
  if (max <= 0) return 0;
  return clamp(value / max, 0, 1);
}

/** 1 - normalize(value, max). 작을수록 좋다는 의미 반전 */
export function invertNormalize(value: number, max = 100): number {
  return 1 - normalize(value, max);
}

/** 경쟁 강도 문자열 → 0~1 수치 */
export function compIdxToNumber(comp: CompetitionLevel | string | null | undefined): number {
  if (comp === '낮음' || comp === '중간' || comp === '높음') {
    return COMP_IDX_NUMERIC[comp];
  }
  return COMP_IDX_NUMERIC['중간'];
}

/** godScore(0~100) → 9단계 등급 */
export function scoreToGrade(score: number): KeywordGrade {
  for (const tier of GRADE_THRESHOLDS) {
    if (score >= tier.min) return tier.grade;
  }
  return 'D';
}

/** 포화도(0~100+) → 5단계 */
export function saturationToGrade(saturation: number): SaturationGrade {
  for (const tier of SATURATION_THRESHOLDS) {
    if (saturation >= tier.min) return tier.grade;
  }
  return '매우낮음';
}

/** 1234567 → "1,234,567" */
export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('ko-KR');
}

/** 0.1234 → "12.3%" */
export function formatPercent(ratio: number, digits = 1): string {
  if (!Number.isFinite(ratio)) return '-';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** ISO 8601 → "2026-05-11 14:30" */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** 안전한 0-나눗셈 */
export function safeDiv(a: number, b: number): number {
  if (!b) return 0;
  return a / b;
}

/** 키워드 정규화: trim + 내부 공백 1칸 + 소문자 (영문) */
export function normalizeKeyword(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * "< 10" 같은 비숫자 문자열을 5로, 숫자/숫자형 문자열은 Number로 변환.
 * 네이버 검색광고 API는 월간 검색량이 10 미만일 때 "< 10" 을 반환한다.
 */
export function coerceNaverCount(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed.startsWith('<')) return 5;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 5;
  }
  return 0;
}

/** 배열을 size 단위로 청크 */
export function chunk<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** YYYY-MM-DD (UTC 기준) — KV 키 일자 prefix 용 */
export function todayKey(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** 다음 UTC 자정까지 남은 초 — KV 카운터 TTL 용 */
export function secondsUntilUtcMidnight(now: Date = new Date()): number {
  const next = new Date(now);
  next.setUTCHours(24, 0, 0, 0);
  return Math.max(60, Math.ceil((next.getTime() - now.getTime()) / 1000));
}

/** unix 초로 환산 */
export function unixNow(now: Date = new Date()): number {
  return Math.floor(now.getTime() / 1000);
}

/** 다음 KST 자정의 unix 초 (사용량 카운터 리셋 기준) */
export function nextKstMidnightUnix(now: Date = new Date()): number {
  // KST = UTC+9
  const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
  const nowKstMs = now.getTime() + KST_OFFSET_MS;
  const kst = new Date(nowKstMs);
  const nextKstMs = Date.UTC(
    kst.getUTCFullYear(),
    kst.getUTCMonth(),
    kst.getUTCDate() + 1,
    0,
    0,
    0,
    0,
  );
  // 위 timestamp는 "KST 자정"을 UTC로 표현한 것의 ms. 다시 UTC로 변환:
  const utcMs = nextKstMs - KST_OFFSET_MS;
  return Math.floor(utcMs / 1000);
}

/** 캐시 신선도 라벨링 (unix 초 기준) */
export function freshnessLabel(
  cachedAtUnix: number,
  nowUnix: number = unixNow(),
): import('./types').FreshnessLabel {
  const diff = nowUnix - cachedAtUnix;
  if (diff < 60) return '실시간';
  if (diff < 60 * 60) return '1시간 이내';
  if (diff < 60 * 60 * 6) return '6시간 이내';
  if (diff < 60 * 60 * 24) return '24시간 이내';
  return '오래됨';
}

/** 32바이트 hex 토큰 (세션 ID 등). Workers의 crypto.getRandomValues 사용 */
export function randomToken(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i]!.toString(16).padStart(2, '0');
  return out;
}
